import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaChevronLeft } from 'react-icons/fa';
import { FiChevronRight } from 'react-icons/fi';

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background-color: ${theme.colors.white};
  position: relative;
`;

const TableBodyWrapper = styled.div<{ showScrollbar?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  
  /* Scrollbar overlay only on body - auto-hide */
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary[400]} transparent;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Hide scrollbar buttons (arrows) */
  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.primary[500]} 100%);
    border-radius: 4px;
    opacity: ${({ showScrollbar }) => showScrollbar ? 0.8 : 0.3};
    transition: opacity 1s ease 1.5s;
  }
  
  &:hover::-webkit-scrollbar-thumb {
    opacity: 0.9;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    opacity: 1;
    background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  }
  
  /* Always show scrollbar when scrolling */
  &.scrolling::-webkit-scrollbar-thumb {
    opacity: 0.8;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const StyledTable = styled.table<{ columnWidths?: string[] }>`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
  background-color: ${theme.colors.white};
  
  @media (max-width: 1200px) {
    width: 1200px;
    min-width: 1200px;
  }
  
  thead {
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-bottom: 2px solid ${theme.colors.primary[200]};
    flex-shrink: 0;
    
    tr:first-child th:first-child {
      border-top-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:first-child th:last-child {
      border-top-right-radius: ${theme.borderRadius.lg};
    }
  }
  
  th {
    text-align: center;
    white-space: normal;
    line-height: 1.4;
    vertical-align: middle;
    font-weight: ${theme.typography.fontWeight.bold};
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.primary[700]};
    text-transform: uppercase;
    letter-spacing: 0.1em;
    position: relative;
    
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background: linear-gradient(180deg, transparent, ${theme.colors.primary[300]}, transparent);
    }
  }
  
  td {
    overflow: hidden;
    text-overflow: ellipsis;
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.light.text.primary};
    vertical-align: middle;
    text-align: center;
  }
  
  /* Center align all cells except first column */
  td:not(:first-child) {
    text-align: center;
  }
  
  /* Keep first column left-aligned */
  td:first-child {
    text-align: left;
  }
  
  /* Column widths - can be customized via columnWidths prop */
  ${({ columnWidths }) => columnWidths && columnWidths.map((width, index) => `
    th:nth-child(${index + 1}), td:nth-child(${index + 1}) { width: ${width}; }
  `).join('')}
  
  /* Row hover effect */
  tbody tr {
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${theme.colors.primary[50]} !important;
    }
    
    &:nth-child(even) {
      background-color: ${theme.colors.white};
    }
    
    &:nth-child(odd) {
      background-color: ${theme.colors.light.background.secondary};
    }
  }
  
  /* Footer styling - sticky with header matching background */
  tfoot {
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-top: 2px solid ${theme.colors.primary[200]};
    flex-shrink: 0;
    
    tr:last-child td:first-child {
      border-bottom-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:last-child td:last-child {
      border-bottom-right-radius: ${theme.borderRadius.lg};
    }
    
    td {
      padding: ${theme.spacing[2]} ${theme.spacing[3]};
      text-align: center;
      position: relative;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[1]};
  width: 100%;
  flex-wrap: wrap;
  padding: ${theme.spacing[1]} 0;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[1]};
  }
`;

const PaginationNavButton = styled.button<{ disabled?: boolean; isNext?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${({ disabled, isNext }) =>
    disabled
      ? theme.colors.light.background.tertiary
      : isNext
        ? theme.colors.primary[600]
        : theme.colors.light.background.tertiary};
  color: ${({ disabled, isNext }) =>
    disabled
      ? theme.colors.light.text.muted
      : isNext
        ? theme.colors.white
        : theme.colors.light.text.secondary};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${({ isNext }) =>
    isNext
      ? theme.colors.primary[700]
      : theme.colors.light.border.medium};
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageNumberButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ active }) => active ? theme.colors.primary[800] : theme.colors.primary[600]};
  background-color: ${({ active }) =>
    active
      ? theme.colors.white
      : theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.white};
    color: ${({ active }) => active ? theme.colors.primary[800] : theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Ellipsis = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  padding: 0 ${theme.spacing[1]};
`;

const PaginationInfo = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.primary[700]};
  font-weight: ${theme.typography.fontWeight.medium};
`;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  showFirstLast?: boolean;
  onFirstPage?: () => void;
  onLastPage?: () => void;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  searchTerm?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  loading = false,
  showFirstLast = false,
  onFirstPage,
  onLastPage,
  hasNextPage,
  hasPrevPage,
  searchTerm,
}) => {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis-start');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <PaginationWrapper>
      <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
        {showFirstLast && onFirstPage && (
          <PaginationNavButton
            onClick={onFirstPage}
            disabled={currentPage === 1 || loading}
            title="First Page"
          >
            <FaChevronLeft size={16} />
            <FaChevronLeft size={16} style={{ marginLeft: -10 }} />
          </PaginationNavButton>
        )}
        <PaginationNavButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={
            searchTerm
              ? currentPage === 1
              : (hasPrevPage !== undefined ? !hasPrevPage : currentPage === 1) || loading
          }
        >
          <FaChevronLeft size={16} />
        </PaginationNavButton>
        {getPageNumbers().map((page, index) => {
          if (page === 'ellipsis-start' || page === 'ellipsis-end') {
            return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
          }
          return (
            <PageNumberButton
              key={page}
              active={currentPage === page}
              onClick={() => onPageChange(page as number)}
              disabled={loading}
            >
              {page}
            </PageNumberButton>
          );
        })}
        <PaginationNavButton
          isNext
          onClick={() => onPageChange(currentPage + 1)}
          disabled={
            searchTerm
              ? currentPage >= totalPages
              : (hasNextPage !== undefined ? !hasNextPage : currentPage >= totalPages) || loading
          }
        >
          <FiChevronRight size={16} />
        </PaginationNavButton>
        {showFirstLast && onLastPage && (
          <PaginationNavButton
            isNext
            onClick={onLastPage}
            disabled={
              (searchTerm
                ? currentPage >= totalPages
                : (hasNextPage !== undefined ? !hasNextPage && currentPage >= totalPages : currentPage >= totalPages)) ||
              loading
            }
            title="Last Page"
          >
            <FiChevronRight size={16} />
            <FiChevronRight size={16} style={{ marginRight: -10 }} />
          </PaginationNavButton>
        )}
      </div>
    </PaginationWrapper>
  );
};

interface DataTableProps {
  header: React.ReactNode;
  body: React.ReactNode;
  pagination?: PaginationProps;
  columnWidths?: string[];
}

export const DataTable: React.FC<DataTableProps> = ({
  header,
  body,
  pagination,
  columnWidths,
}) => {
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tableBody = tableBodyRef.current;
    if (!tableBody) return;

    const handleScroll = () => {
      setShowScrollbar(true);
      tableBody.classList.add('scrolling');
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollbar(false);
        tableBody.classList.remove('scrolling');
      }, 2000);
    };

    tableBody.addEventListener('scroll', handleScroll);
    tableBody.addEventListener('mouseenter', () => {
      setShowScrollbar(true);
      tableBody.classList.add('scrolling');
    });
    tableBody.addEventListener('mouseleave', () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollbar(false);
        tableBody.classList.remove('scrolling');
      }, 2000);
    });

    return () => {
      tableBody.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TableWrapper>
      <StyledTable columnWidths={columnWidths}>
        <thead>{header}</thead>
      </StyledTable>
      <TableBodyWrapper ref={tableBodyRef} showScrollbar={showScrollbar}>
        <StyledTable columnWidths={columnWidths}>
          <tbody>{body}</tbody>
        </StyledTable>
      </TableBodyWrapper>
      {pagination && (
        <StyledTable columnWidths={columnWidths}>
          <tfoot>
            <tr>
              <td colSpan={100}>
                <Pagination {...pagination} />
              </td>
            </tr>
          </tfoot>
        </StyledTable>
      )}
    </TableWrapper>
  );
};

export default DataTable;

